/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { jsx } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import { Box, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { type AuditLogEventData } from '@atlassian/audit-logs-common';

import { DetailsRow } from '../../../../../common/utils/components/details-row';
import { DetailsSection } from '../../../../../common/utils/components/details-section';

const valueStyles = cssMap({
	oldValue: {
		display: 'inline',
		backgroundColor: token('color.background.accent.red.subtlest'),
	},
	oldValueText: {
		textDecorationLine: 'line-through',
	},
	newValue: {
		display: 'inline',
		backgroundColor: token('color.background.accent.green.subtlest'),
	},
});

const messages = defineMessages({
	changeComparison: {
		id: 'organization.audit.log.side-panel.tab-panels.details.change-comparison-section.title',
		defaultMessage: 'Change comparison',
		description: 'Title for the change comparison section in audit log details panel',
	},
	oldValue: {
		id: 'organization.audit.log.side-panel.tab-panels.details.change-comparison-section.old-value.label',
		defaultMessage: 'Previous value',
		description: 'Label for the old value field in change comparison section',
	},
	newValue: {
		id: 'organization.audit.log.side-panel.tab-panels.details.change-comparison-section.new-value.label',
		defaultMessage: 'New value',
		description: 'Label for the new value field in change comparison section',
	},
});

interface ChangeComparisonSectionProps {
	event: AuditLogEventData;
}

export const ChangeComparisonSection = ({
	event,
}: ChangeComparisonSectionProps): JSX.Element | null => {
	const { formatMessage } = useIntl();

	if (!event || (!event.oldValue && !event.newValue)) {
		return null;
	}

	return (
		<DetailsSection title={formatMessage(messages.changeComparison)}>
			{event.oldValue !== undefined && (
				<DetailsRow
					label={formatMessage(messages.oldValue)}
					value={
						<Box as="span">
							<Text xcss={valueStyles.oldValueText}>
								<Box xcss={valueStyles.oldValue}>{event.oldValue}</Box>
							</Text>
						</Box>
					}
				/>
			)}
			{event.newValue !== undefined && (
				<DetailsRow
					label={formatMessage(messages.newValue)}
					value={
						<Box as="span">
							<Text>
								<Box xcss={valueStyles.newValue}>{event.newValue}</Box>
							</Text>
						</Box>
					}
				/>
			)}
		</DetailsSection>
	);
};
